import { Permission } from '@vitesse/proto/vitesse/v1/entitlements_pb.js';
import { registerClientTools, type ClientTool, type ToolOutcome } from '@vitesse/base-ui/ai';
import type { GatewayEntry } from '../managers/gateway-entry';
import { GATEWAY_PARAM, resolveGateway, type GatewayScope } from './gateway-resolve';
import type { BackendClient } from '../bridge/backend-client';
import { categorize } from '../shell/vitesse-value-editor';

/*
 * WHAT A SYMBOL DID, not just what it is doing.
 *
 * Every other plant tool answers about NOW: search the catalogue, read a value,
 * write one. That left the assistant unable to answer the question people
 * actually ask — "why did it stop", "was it climbing before it tripped", "is this
 * normal for a Tuesday" — because each of those is a question about the past, and
 * the past was only reachable by a person opening the History tab.
 *
 * It matters most for the program analyst, whose whole job is explaining what a
 * program DOES. Static structure says a rung writes a symbol; it cannot say the
 * symbol went false four seconds before the fault. The role already carried
 * history:read and nothing could use it — a permission with no tool behind it is
 * a claim, not a capability.
 *
 * ONE TOOL, TWO RESOLUTIONS. Raw samples answer "what exactly happened at 14:32";
 * buckets answer "what did the last week look like" without returning a week of
 * samples to a model that has to read them. Which one is chosen by the CALLER
 * naming a bucket size, because only the caller knows which question it is asking
 * — and the wrong choice is not a failure, it is an answer at the wrong
 * resolution, which is worse because it looks fine.
 */

export interface HistoryToolDeps extends GatewayScope {
  backend(): BackendClient | undefined;
}

/** Enough points to describe a shape, few enough to fit a prompt. Past this the
 *  model is being handed a spreadsheet and asked to be a chart. */
const MAX_POINTS = 200;

const noBackend: ToolOutcome = { ok: false, message: 'The backend is not ready yet — try again in a moment.' };

/** The value kinds this tool can return — what the chart read underneath keeps (bool → 0/1). */
const CARRIES = new Set(['boolean', 'integer', 'number']);

/**
 * WHAT THIS TOOL KNOWS IT DROPS: text and structured values, named as such.
 *
 * Not "anything `categorize` does not call a number". That mapper is for the value editor and sends
 * every type it does not recognise to free text — S7 addresses ("DBX1.0", "M0.7"), TIME, a vendor's
 * own INT32 — and what is dropped here is decided by the value on the wire, not the declared type.
 * Told those were "not shown", the model reported a bit that was genuinely unrecorded as one whose
 * samples exist. An unrecognised type says nothing either way, so it gets all three possibilities.
 */
const TEXT_TYPE = /^W?(STRING|CHAR)(\[\d+\])?$/i;
const notCarried = (dataType: string): boolean => categorize(dataType) === 'object' || TEXT_TYPE.test(dataType.trim());

/**
 * A symbol's catalogue type, or '' when it cannot be established — the lookup
 * `symbols.write` makes, asked only to explain an empty result. Never throws:
 * a failed lookup leaves the sentence as it was, it does not fail the read.
 */
async function symbolType(backend: BackendClient, gatewayId: string, controllerId: string, path: string): Promise<string> {
  try {
    const page = await backend.api.fetchSymbolPage(gatewayId, controllerId, { fts: path, pageSize: 50 });
    const hit = page.rows.find((r: { path: string }) => r.path === path) as { dataType?: string } | undefined;
    return hit?.dataType ?? '';
  } catch {
    return '';
  }
}

function findController(entry: GatewayEntry, idOrName: string) {
  const wanted = idOrName.trim().toLowerCase();
  return entry.controllers.all.find((c) => c.id.toLowerCase() === wanted || c.name.toLowerCase() === wanted);
}

export function registerHistoryTools(deps: HistoryToolDeps): () => void {
  const query: ClientTool = {
    name: 'history.query',
    /*
     * AND HOW TO SHOW IT. Reading the past and showing it are two acts, and the second had no words
     * anywhere: no assistant ever opened the History tab, it described curves the person could have
     * been looking at. Said here, with the tool that reads history, because whoever holds this tool
     * is the one talking about the past. Every specialist that holds it also holds platform.navigate,
     * including ones working on a task out of sight; the sentence asks for the person wanting to
     * look, and whether a specialist should ever move the screen is not settled here.
     */
    description:
      'Read what a symbol DID over a past time window, from the historian. Use it for anything about the past — why something ' +
      'stopped, whether a value was climbing before a fault, what a normal shift looks like. Give `minutesAgo` for the window ' +
      'and, for anything longer than a few minutes, a `bucketSeconds` so you get a min/max/avg envelope per bucket instead of ' +
      'thousands of raw samples. Omit `bucketSeconds` only when you need the exact samples around a specific moment. ' +
      "This reads the past for YOU; to SHOW it to the person when they want to look at a symbol's past, also open it with " +
      'platform.navigate at /gateways/<relay id>/controllers/<controller id>?tab=history&symbol=<path>&symbol=<path>&minutes=<n> — ' +
      "the relay's id, not its name; up to three symbols, each exactly as `symbols.search` returned it, URL-encoded; and the " +
      'window you read — so they can zoom and compare there.',
    mutates: false,
    // Historian/QueryRange and QueryAggregated both require it. Same gate,
    // whether a person opens the History tab or the assistant asks.
    permission: Permission.HISTORY_READ,
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        controller: { type: 'string', description: 'Controller id or name, as shown on the relay.' },
        symbolPath: { type: 'string', description: 'The symbol path, exactly as `symbols.search` returned it.' },
        minutesAgo: { type: 'number', description: 'How far back to look, in minutes. Defaults to 60.' },
        bucketSeconds: {
          type: 'number',
          description:
            'Seconds per bucket for a min/max/avg envelope. Omit for raw samples. A window of an hour wants ~30; a day wants ~600.',
        },
      },
      required: ['controller', 'symbolPath'],
      additionalProperties: false,
    },
    async run(args): Promise<ToolOutcome> {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const backend = deps.backend();
      if (!backend) return noBackend;

      const controller = findController(entry, String(args.controller ?? ''));
      if (!controller) {
        const known = entry.controllers.all.map((c) => `${c.name} (${c.id})`);
        return {
          ok: false,
          message: `No controller "${String(args.controller)}" on this relay.${known.length ? ` The controllers here are: ${known.join(', ')}.` : ''}`,
        };
      }

      const symbolPath = String(args.symbolPath ?? '').trim();
      if (!symbolPath) return { ok: false, message: 'Name the symbol to read — `symbols.search` returns the exact path.' };

      const minutes = Number(args.minutesAgo) > 0 ? Number(args.minutesAgo) : 60;
      const bucket = Number(args.bucketSeconds) > 0 ? Number(args.bucketSeconds) : null;
      const t2 = Date.now();
      const t1 = t2 - minutes * 60_000;

      try {
        const buckets = await backend.api.historyQuery(gw.value.id, controller.id, symbolPath, t1, t2, bucket);
        if (!buckets.length) {
          /*
           * AN EMPTY WINDOW IS AN ANSWER, BUT IT IS NOT THE ANSWER THE MODEL USED TO GIVE.
           *
           * This said "no recorded history for X … the historian may not have been recording then",
           * which reads as a statement about the SYMBOL. Observed live: three empty windows on a
           * controller whose history stops six days before the window asked about, and the model
           * concluded "the historian isn't recording these telemetry symbols" and "there simply is
           * no past to look back at for this controller" — while the store held 494,887 rows for
           * exactly that symbol. Every query was correct; the sentence describing the result was
           * not, and a wrong sentence about an empty result is worse than the empty result, because
           * the reader acts on the sentence.
           *
           * So say WHICH emptiness it is. The bounds are one cached call and already exist for the
           * History tab's shading; they distinguish "nothing was ever recorded here" from "there is
           * a past, and you asked after it ended".
           */
          const bounds = await backend.api.historyBounds(gw.value.id, controller.id);
          const window = `the last ${minutes} minutes`;
          const base = { controller: controller.id, symbolPath, samples: [] as never[] };

          if (!bounds) {
            // The bounds call failed. Claim only what was established: this window is empty.
            return {
              ok: true,
              message: `No samples for ${symbolPath} on ${controller.name} in ${window}. I could not check whether history exists outside that window, so this is not evidence that the symbol has never been recorded.`,
              data: base,
            };
          }
          if (!bounds.hasData) {
            return {
              ok: true,
              message: `${controller.name} has no recorded history at all — not for ${symbolPath}, and not for anything else on it. The historian has never stored samples for this controller.`,
              data: { ...base, controllerHasHistory: false },
            };
          }

          const from = new Date(bounds.minTsMs).toISOString();
          const to = new Date(bounds.maxTsMs).toISOString();
          const span = { controllerHistoryFromIso: from, controllerHistoryToIso: to, controllerHasHistory: true };
          if (t1 > bounds.maxTsMs) {
            // The single case that produced the false conclusion.
            return {
              ok: true,
              message: `No samples in ${window}, because ${controller.name} stopped recording before that window began: its history runs ${from} to ${to}. History for ${symbolPath} may well exist earlier — ask for a window that ends at or before ${to}.`,
              data: { ...base, ...span },
            };
          }
          if (t2 < bounds.minTsMs) {
            return {
              ok: true,
              message: `No samples in ${window}, because that window ends before ${controller.name} began recording: its history runs ${from} to ${to}.`,
              data: { ...base, ...span },
            };
          }
          // The window DOES overlap recorded history, so the gap is this symbol's.
          /*
           * …UNLESS THE SAMPLES WERE THERE AND THIS TOOL CANNOT SHOW THEM.
           *
           * The historian read underneath is the chart's, and a chart draws numbers: text and
           * object samples are dropped before they get here (connect-client `queryRange`). So a
           * STRING symbol that changed every minute came back empty, the sentence below called it
           * "not recorded, or did not change", and the assistant reported a status signal as
           * never logged. The catalogue knows the type — the same lookup `symbols.write` makes —
           * so when the symbol is one this tool cannot carry, say exactly that. Asked only here,
           * on the one empty result it can explain; when the lookup fails, the sentence names all
           * three possibilities instead of guessing between them.
           */
          const dataType = await symbolType(backend, gw.value.id, controller.id, symbolPath);
          if (dataType && notCarried(dataType)) {
            return {
              ok: true,
              message: `${symbolPath} is a ${dataType} symbol, and this tool returns numbers and booleans only, so its samples are not shown here. This is not evidence that it was unrecorded or unchanged during ${window}.`,
              data: { ...base, ...span, dataType, numericOnly: true },
            };
          }
          return {
            ok: true,
            message: `No samples for ${symbolPath} in ${window}, even though ${controller.name} has history covering ${from} to ${to}. ${dataType && CARRIES.has(categorize(dataType)) ? 'Either this symbol is not recorded, or it did not change during the window.' : 'Either this symbol is not recorded, it did not change during the window, or its values are not numbers (this tool returns numbers and booleans only).'}`,
            data: { ...base, ...span, ...(dataType ? { dataType } : {}) },
          };
        }
        // Trimmed from the END, keeping the most recent: a truncated answer that
        // drops the newest points is the wrong half of the window for almost
        // every question anyone asks of it.
        const trimmed = buckets.slice(-MAX_POINTS);
        const dropped = buckets.length - trimmed.length;
        return {
          ok: true,
          message:
            `${trimmed.length} ${bucket ? `${bucket}s buckets` : 'samples'} for ${symbolPath} on ${controller.name}, ` +
            `covering the last ${minutes} minutes` +
            (dropped ? `. ${dropped} older points were dropped to fit — ask for a coarser bucketSeconds to see the whole window.` : '.'),
          data: { controller: controller.id, symbolPath, bucketSeconds: bucket, points: trimmed },
        };
      } catch (e) {
        // historyQuery THROWS rather than returning empty, precisely so a failed
        // fetch cannot be mistaken for a confirmed gap. Passed through as a
        // failure so the model retries or says it could not look, instead of
        // reporting an absence of data that was never established.
        if (e instanceof Error && e.name === 'HistoryQueryTimeout') {
          // A slow read, not a missing past — and the same query again will be just as slow. Raw
          // samples over a long window are the usual cause: an hour of a fast signal is a hundred
          // thousand rows, read in full to show the newest two hundred.
          return {
            ok: false,
            message:
              `The historian did not answer in time for ${symbolPath} over the last ${minutes} minutes (${e.message}). ` +
              `This is a slow read, not a missing past. Ask again with ${bucket ? 'a shorter window or a larger bucketSeconds' : 'a bucketSeconds (or a shorter window)'}.`,
          };
        }
        return { ok: false, message: `Could not read history: ${e instanceof Error ? e.message : String(e)}` };
      }
    },
  };

  return registerClientTools(query);
}
